/**
 * July 4 Sale — sidecart bandana / saver-bag gift-with-purchase + tracker.
 *
 * Cleanup tag: JULY4-SALE-2026
 * Spec: docs/sale-july4-2026/README.md
 *
 * Loaded only while settings.july4_sale_enabled is on (see layout/theme.liquid),
 * and it SUPERSEDES the evergreen $55 saver-bag module, which isn't loaded during
 * the sale. Behaviour:
 *
 *   • Reads today's order count (window.__TEC_BANDANA, written from a shop metafield
 *     the Shopify Flow maintains). remaining = max(0, cap - count).
 *   • Every non-empty, non-excluded cart auto-gets a free gift, no spend minimum:
 *       remaining > 0  → bandana   (first 250 orders/day)
 *       remaining == 0 → fallback  (saver bag now, tote later — config-swappable)
 *   • Renders a 3-state tracker (active / locked-in / depleted) + a "free shipping
 *     ends in" ticker into the cart drawer, adapted to our sidecart styling.
 *   • Carts containing one of the 4 locked offer PDPs (excludedHandles) get no gift
 *     and no sale UI — those funnels are dedicated / not combined.
 *
 * Gifts are real products zeroed by a Shopify "100% off products" automatic discount
 * configured in admin (this code only adds/removes the line). A gift already locked in
 * is never yanked just because the day's bandanas ran out mid-session.
 *
 * Plumbing (cart fetch, AJAX re-sweep, anti-flicker observer, add/remove) mirrors the
 * proven assets/ab-sidecart-free-gift.js, kept separate so the sale rips out cleanly.
 */
(function () {
  'use strict';

  var SALE = (typeof window !== 'undefined' && window.__TEC_SALE) || {};
  var BANDANA = (typeof window !== 'undefined' && window.__TEC_BANDANA) || {};

  var DEBUG = false;
  var PREFIX = '[TEC-July4]';
  function log() { if (DEBUG) console.log.apply(console, [PREFIX].concat([].slice.call(arguments))); }

  var bandanaGift = SALE.bandana || {};
  var fallbackGift = SALE.fallback || {};
  var FALLBACK_LABEL = fallbackGift.label || 'saver bag';
  // Locked offer PDPs — primarily matched on product_id (stable), with handle fallback.
  var EXCLUDED_IDS = ((SALE.excludedIds || [])
    .map(function (n) { return Number(String(n).trim()); })
    .filter(function (n) { return n > 0; }));
  var EXCLUDED = ((SALE.excludedHandles || [])
    .map(function (h) { return (h || '').trim().toLowerCase(); })
    .filter(Boolean));

  // The two variant IDs we manage. 0 = not configured yet (preview before IDs land):
  // the tracker still renders, we just can't add a real gift line.
  var BANDANA_VID = Number(bandanaGift.variantId) || 0;
  var FALLBACK_VID = Number(fallbackGift.variantId) || 0;

  function cap() { return Number(BANDANA.cap) || 250; }
  function soldToday() { return Math.max(0, Number(BANDANA.count) || 0); }
  function remaining() { return Math.max(0, cap() - soldToday()); }

  // ─── State ───────────────────────────────────────────────────────────────────
  var productCache = new Map();
  var lastCart = null;
  var lastCartFetch = 0;
  var lastState = null;
  var sweepInFlight = false;
  var mutating = false;

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache.has(handle)) return Promise.resolve(productCache.get(handle));
    return fetch('/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { productCache.set(handle, p); return p; })
      .catch(function () { productCache.set(handle, null); return null; });
  }

  function fetchCart() {
    var now = Date.now();
    if (lastCart && now - lastCartFetch < 250) return Promise.resolve(lastCart);
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { lastCart = cart; lastCartFetch = Date.now(); return cart; });
  }

  function isGiftVariant(vid) {
    return (BANDANA_VID && vid === BANDANA_VID) || (FALLBACK_VID && vid === FALLBACK_VID);
  }

  // ─── Cart math (pure) ──────────────────────────────────────────────────────
  function computeState(cart) {
    var items = (cart && cart.items) || [];
    var bandanaKey = null, fallbackKey = null, paidCount = 0, hasExcluded = false, hasSaver = false;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      // The bandana is uniquely ours (Loop never gives it) → any bandana line is ours.
      if (BANDANA_VID && it.variant_id === BANDANA_VID) { bandanaKey = it.key; continue; }
      // The saver bag is ALSO Loop's subscription gift (same variant). Note one exists,
      // but only treat it as OUR depleted-fallback if it carries our marker — otherwise
      // Loop's saver bag would falsely read as "fallback given → bandanas gone".
      if (FALLBACK_VID && it.variant_id === FALLBACK_VID) {
        hasSaver = true;
        if (it.properties && it.properties._tec_july4 === 'saver') fallbackKey = it.key;
        continue;
      }
      paidCount += it.quantity;
      if (EXCLUDED_IDS.indexOf(it.product_id) !== -1) hasExcluded = true;
      if (it.handle && EXCLUDED.indexOf(String(it.handle).toLowerCase()) !== -1) hasExcluded = true;
    }
    // Cart-level exclusion is driven entirely by excludedHandles (live, from /cart.js)
    // so it clears the moment the locked product leaves the cart. The server-rendered
    // SALE.cartHasExcluded is page-load-only (would go stale after an AJAX remove), so
    // it's intentionally not used here — keep the excluded-handles setting populated.

    var rem = remaining();
    var phase;
    if (hasExcluded) phase = 'excluded';
    else if (paidCount === 0) phase = 'empty';
    else if (bandanaKey !== null) phase = 'locked';
    else if (fallbackKey !== null) phase = 'depleted_locked';
    else if (rem > 0) phase = 'active';
    else phase = 'depleted';

    return {
      phase: phase,
      remaining: rem,
      sold: soldToday(),
      cap: cap(),
      paidCount: paidCount,
      hasExcluded: hasExcluded,
      hasSaver: hasSaver,
      bandanaKey: bandanaKey,
      fallbackKey: fallbackKey,
      anyGiftKey: bandanaKey !== null ? bandanaKey : fallbackKey
    };
  }

  // ─── Cart writes ──────────────────────────────────────────────────────────────
  function publishCartUpdate() {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'july4-gwp' });
      }
    } catch (e) {}
  }

  // isBandana=true → if the add fails (e.g. bandana out of stock), give the fallback gift instead.
  function addGift(vid, isBandana) {
    if (mutating || !vid) return;
    mutating = true;
    log('add gift', vid);
    fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      // No selling_plan → always a one-time line, never a subscription.
      // Tag the line with our marker so computeState can tell OUR gift apart from Loop's
      // subscription saver bag (same variant). Underscore prefix → hidden in cart/checkout.
      // Ask for the drawer sections so the new gift line renders immediately (otherwise
      // /cart.js updates but the theme's line items stay stale — "locked in" with no line).
      body: JSON.stringify({ items: [{ id: vid, quantity: 1, properties: { _tec_july4: isBandana ? 'bandana' : 'saver' } }], sections: DRAWER_SECTIONS, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
      .then(function (res) {
        lastCart = null;
        mutating = false;
        if (!res.ok && isBandana && FALLBACK_VID && FALLBACK_VID !== vid) {
          // Bandana add failed — but only give the saver bag if NO gift is already in the
          // cart. The failure is often "you already have the last bandana" (1-unit stock,
          // or a parallel add won), in which case adding the fallback would double-gift.
          log('bandana add failed — re-checking before fallback', res.body);
          return fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (c) {
              var items = c.items || [];
              var hasOurGift = items.some(function (i) {
                var m = i.properties && i.properties._tec_july4;
                return i.variant_id === BANDANA_VID || (i.variant_id === FALLBACK_VID && m === 'saver');
              });
              if (hasOurGift) { setTimeout(sweep, 200); return; }
              // Only hand out the saver bag on a GENUINE depletion, and never stack a
              // second one on top of a saver bag Loop already gave a subscription cart.
              var saverPresent = items.some(function (i) { return i.variant_id === FALLBACK_VID; });
              if (remaining() <= 0 && !saverPresent) { addGift(FALLBACK_VID, false); return; }
              setTimeout(sweep, 200);
            })
            .catch(function () { setTimeout(sweep, 200); });
        }
        renderDrawer(res.body);
        publishCartUpdate();
        setTimeout(sweep, 200);
      })
      .catch(function (e) { mutating = false; console.error(PREFIX, 'addGift', e); });
  }

  // Hand a /cart/add|change "sections" response to the theme so it re-renders the drawer
  // line items (keeps the visible cart in sync with what we just added/removed).
  var DRAWER_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  function renderDrawer(response) {
    if (!response || !response.sections) return;
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      try { drawer.renderContents(response); } catch (e) { log('renderContents failed', e); }
    }
  }

  function removeGift(key) {
    if (mutating || !key) return Promise.resolve();
    mutating = true;
    log('remove gift', key);
    return fetch('/cart/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: 0, sections: DRAWER_SECTIONS, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (body) { lastCart = null; renderDrawer(body); publishCartUpdate(); })
      .catch(function (e) { console.error(PREFIX, 'removeGift', e); })
      .then(function () { mutating = false; });
  }

  // ─── Renderers ─────────────────────────────────────────────────────────────
  var STAR = '★';
  // Theme checkmark (verbatim from assets/icon-green-checkmark.svg).
  var CHECK = '<svg class="sale-tracker__check" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" fill="#6BBE66" d="M48,0c26.51,0,48,21.49,48,48S74.51,96,48,96S0,74.51,0,48 S21.49,0,48,0L48,0z M26.764,49.277c0.644-3.734,4.906-5.813,8.269-3.79c0.305,0.182,0.596,0.398,0.867,0.646l0.026,0.025 c1.509,1.446,3.2,2.951,4.876,4.443l1.438,1.291l17.063-17.898c1.019-1.067,1.764-1.757,3.293-2.101 c5.235-1.155,8.916,5.244,5.206,9.155L46.536,63.366c-2.003,2.137-5.583,2.332-7.736,0.291c-1.234-1.146-2.576-2.312-3.933-3.489 c-2.35-2.042-4.747-4.125-6.701-6.187C26.993,52.809,26.487,50.89,26.764,49.277L26.764,49.277z"/></svg>';

  function trackerCopy(state) {
    // Bar shows bandanas REMAINING (starts full, drains as they're claimed — right-anchored).
    var fillPct = Math.max(0, Math.min(100, Math.round(state.remaining / state.cap * 100)));
    var label = '', msg, mod = '';
    switch (state.phase) {
      case 'locked':
        // Bandana actually in the cart.
        mod = 'is-locked';
        msg = '<span class="sale-tracker__lockrow">' + CHECK +
              '<span><strong>Bandana locked in.</strong> Cleaner shower, cleaner planet.</span></span>';
        break;
      case 'depleted':
      case 'depleted_locked':
        // Today's bandanas are gone (remaining 0 → empty bar) — the fallback gift is on us.
        mod = 'is-depleted';
        msg = 'Today’s bandanas are gone — your free <strong>' + FALLBACK_LABEL +
              '</strong> is on us.<br>New bandana drop at <strong>midnight</strong>.';
        break;
      default: // active / empty
        label = 'Free bandanas left today: <span class="sale-tracker__num">' + state.remaining + '</span>';
        msg = 'Add to cart and check out to lock in your <em>free bandana</em>.';
    }
    return { label: label, msg: msg, fillPct: fillPct, mod: mod };
  }

  // Restore the drawer header to its native state (used in the excluded phase).
  function restoreNativeHeader(header) {
    header.classList.remove('sale-header-ticker');
    var injected = header.querySelector('.sale-ticker');
    if (injected) injected.parentNode.removeChild(injected);
    header.querySelectorAll('.drawer__header__item').forEach(function (i) { i.style.display = ''; });
  }

  function renderTracker(state) {
    var header = document.querySelector('#CartDrawer .drawer__header');
    if (!header) return;
    var nativeMsg = document.querySelector('#CartDrawer .drawer__message:not(.sale-tracker)');

    // Locked offer PDP in cart → no sale UI; hand the drawer back to the theme.
    if (state.phase === 'excluded') {
      restoreNativeHeader(header);
      if (nativeMsg) nativeMsg.style.display = '';
      var oldWrap = document.querySelector('#CartDrawer .sale-tracker-wrap');
      if (oldWrap) oldWrap.parentNode.removeChild(oldWrap);
      return;
    }

    // ── Merge the header into the brown countdown bar (keep the close button) ──
    if (nativeMsg) nativeMsg.style.display = 'none';
    header.classList.add('sale-header-ticker');
    // Hide the native chips (FREE SHIPPING etc.) — the countdown replaces them.
    header.querySelectorAll('.drawer__header__item').forEach(function (i) { i.style.display = 'none'; });

    var ended = window.SaleCountdown && window.SaleCountdown.ended;
    var hasClock = window.SaleCountdown && window.SaleCountdown.hasEnd && !ended;
    var clock = hasClock ? window.SaleCountdown.format(window.SaleCountdown.remaining()) : '';
    var ticker = header.querySelector('.sale-ticker');
    if (hasClock) {
      if (!ticker) {
        ticker = el('div', 'sale-ticker', '');
        header.appendChild(ticker);
      }
      ticker.innerHTML =
        '<span class="sale-ticker__label">Free shipping ends in</span>' +
        '<span class="sale-ticker__clock" data-sale-countdown>' + clock + '</span>';
    } else if (ticker) {
      ticker.parentNode.removeChild(ticker);
    }

    // ── Green tracker block (no separate ticker — it now lives in the header) ──
    var host = document.querySelector('#CartDrawer .sale-tracker-wrap');
    if (!host) {
      host = el('div', 'sale-tracker-wrap');
      header.insertAdjacentElement('afterend', host);
    }
    var c = trackerCopy(state);
    host.innerHTML =
      '<div class="sale-tracker ' + c.mod + '">' +
        (c.label
          ? '<div class="sale-tracker__label">' + c.label + '</div>' +
            '<div class="sale-tracker__bar"><span style="width:' + c.fillPct + '%"></span></div>'
          : '') +
        '<div class="sale-tracker__msg">' + c.msg + '</div>' +
      '</div>';
  }

  // Gift-line "Your free gift" tag removed per request — no line decoration.
  function decorateGiftLine() {}

  // ─── Orchestrator ────────────────────────────────────────────────────────────
  function sweep() {
    if (sweepInFlight || mutating) return;
    sweepInFlight = true;
    fetchCart()
      .then(function (cart) {
        var state = computeState(cart);
        lastState = state;
        log('state', state);

        // Locked offer PDP in cart → no gift, no sale UI.
        if (state.phase === 'excluded') {
          renderTracker(state);
          if (state.bandanaKey) return removeGift(state.bandanaKey).then(function () { setTimeout(sweep, 200); });
          if (state.fallbackKey) return removeGift(state.fallbackKey).then(function () { setTimeout(sweep, 200); });
          return;
        }

        // Cart emptied of paid items → drop a lone free gift.
        if (state.phase === 'empty') {
          if (state.anyGiftKey) return removeGift(state.anyGiftKey).then(function () { setTimeout(sweep, 200); });
          renderTracker(state);
          return;
        }

        // Never keep both gifts. If a bandana is locked in, drop a stray fallback (saver bag).
        if (state.bandanaKey !== null && state.fallbackKey !== null) {
          return removeGift(state.fallbackKey).then(function () { setTimeout(sweep, 200); });
        }

        renderTracker(state);
        decorateGiftLine(state);

        // Auto-add the bandana while they last; the fallback gift once they're gone
        // (or if the bandana add fails because it's out of stock — handled in addGift).
        if (state.bandanaKey === null && state.fallbackKey === null) {
          if (state.remaining > 0 && BANDANA_VID) addGift(BANDANA_VID, true);
          else if (state.remaining === 0 && FALLBACK_VID && !state.hasSaver) addGift(FALLBACK_VID, false);
        }
      })
      .catch(function (err) { console.error(PREFIX, 'sweep', err); })
      .then(function () { sweepInFlight = false; watchDrawer(); });
  }

  // ─── AJAX patch — re-sweep after any /cart write ───────────────────────────
  var CART_WRITE_RE = /\/cart\/(change|add|update|clear)(\.js)?(\?|$)/;
  function patchAjax() {
    var origFetch = window.fetch;
    if (origFetch && !origFetch.__july4Patched) {
      window.fetch = function () {
        var args = arguments;
        var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        if (!CART_WRITE_RE.test(url)) return origFetch.apply(this, args);
        return origFetch.apply(this, args).then(function (res) {
          lastCart = null;
          setTimeout(sweep, 60);
          setTimeout(sweep, 350);
          return res;
        });
      };
      window.fetch.__july4Patched = true;
    }
  }

  // ─── Anti-flicker — re-apply our blocks after the theme rebuilds the drawer ───
  function reapplyFromCache() {
    if (!lastState) return;
    if (lastState.phase === 'excluded') return;
    var header = document.querySelector('#CartDrawer .drawer__header');
    var wrapGone = !document.querySelector('#CartDrawer .sale-tracker-wrap');
    var headerGone = header && !header.classList.contains('sale-header-ticker');
    if (wrapGone || headerGone) renderTracker(lastState);
    decorateGiftLine(lastState);
  }

  function watchDrawer() {
    var target = document.getElementById('CartDrawer');
    if (!target || target.__july4Watched) return;
    target.__july4Watched = true;
    new MutationObserver(reapplyFromCache).observe(target, { childList: true, subtree: true });
  }

  // ─── Injected styles — sidecart-native, brand palette + Nitti ────────────────
  function injectStyles() {
    if (document.getElementById('july4-sale-styles')) return;
    var s = document.createElement('style');
    s.id = 'july4-sale-styles';
    s.textContent = [
      '.sale-tracker-wrap{flex-shrink:0;}',
      '.sale-flag-stripe{height:3px;background:linear-gradient(90deg,#9b2b2b 0 33.3%,#f7efdc 33.3% 66.6%,#2a3a6b 66.6% 100%);opacity:.8;}',
      '.sale-tracker{padding:14px 20px 16px;background:#eef3e6;color:#3b332f;}',
      '.sale-tracker.is-locked{background:#dce7cc;}',
      '.sale-tracker.is-depleted{background:#EFE3DD;}',
      '.sale-tracker__label{font-family:"Nitti",Georgia,serif;text-transform:uppercase;letter-spacing:.04em;font-size:12px;font-weight:700;color:#2f3d24;display:flex;align-items:baseline;gap:6px;}',
      '.sale-tracker.is-depleted .sale-tracker__label{color:#a36b3d;}',
      '.sale-tracker__num{font-family:Georgia,serif;font-size:20px;line-height:1;text-transform:none;letter-spacing:0;}',
      '.sale-tracker__bar{height:8px;border-radius:99px;background:rgba(47,61,36,.14);overflow:hidden;margin:10px 0;}',
      '.sale-tracker__bar>span{display:block;height:100%;border-radius:99px;background:linear-gradient(90deg,#4a5d3a,#2f3d24);transition:width .4s ease;}',
      '.sale-tracker.is-depleted .sale-tracker__bar>span{background:linear-gradient(90deg,#a36b3d,#b5532a);opacity:.6;}',
      '.sale-tracker__msg{font-size:13.5px;line-height:1.45;}',
      '.sale-tracker__msg em{font-style:italic;color:#2f3d24;}',
      '.sale-tracker__lockrow{display:flex;align-items:center;gap:8px;}',
      '.sale-tracker__check{width:18px;height:18px;flex-shrink:0;display:block;}',
      '.sale-ticker{display:flex;align-items:center;justify-content:space-between;padding:11px 20px;background:#3b332f;color:#f7efdc;}',
      '.sale-ticker__label{font-family:"Nitti",sans-serif;text-transform:uppercase;letter-spacing:.1em;font-size:13px;font-weight:600;}',
      '.sale-ticker__clock{font-family:"Nitti",monospace;font-size:13px;font-weight:600;letter-spacing:.06em;background:rgba(255,255,255,.1);padding:4px 10px;border-radius:3px;}',
      // Merged header: the drawer header becomes the brown countdown bar (close button kept).
      '#CartDrawer .drawer__header.sale-header-ticker{background:#3b332f !important;height:auto;min-height:55px;}',
      '#CartDrawer .drawer__header.sale-header-ticker .drawer__close{color:#f7efdc;z-index:2;}',
      '#CartDrawer .drawer__header.sale-header-ticker .drawer__close svg{fill:#f7efdc;}',
      '#CartDrawer .drawer__header.sale-header-ticker .drawer__close svg path{fill:#f7efdc;stroke:#f7efdc;}',
      '#CartDrawer .drawer__header.sale-header-ticker .sale-ticker{flex:1;background:transparent;padding:11px 20px 11px 52px;}'
    ].join('');
    document.head.appendChild(s);
  }

  // ─── Init ──────────────────────────────────────────────────────────────────
  function init() {
    injectStyles();
    sweep();
    patchAjax();
    watchDrawer();
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { lastCart = null; watchDrawer(); sweep(); });
      }
    } catch (e) {}
    // Keep the ticker's hide-on-end behaviour in sync if the clock expires mid-session.
    document.addEventListener('tec:sale-countdown-ended', function () { if (lastState) renderTracker(lastState); });
    log('init complete');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', function () {
    setTimeout(function () { sweep(); watchDrawer(); }, 300);
    setTimeout(function () { sweep(); watchDrawer(); }, 1500);
  });
  [600, 1200, 2500].forEach(function (ms) { setTimeout(function () { sweep(); watchDrawer(); }, ms); });

  window.TECJuly4 = { sweep: sweep, state: function () { return lastState; } };
})();
